/**
 * Apply design-system style mutations to a rich-text HTML string.
 *
 * IMPORTANT: keep in sync with
 * static-library/src/shared/utils/parse-text-entities.ts
 *
 * The editor mutates entity content on save with this exact logic; the static
 * library re-applies it locally inside the design-system preview iframe so
 * what the user sees in preview matches what gets persisted.
 */

export type ParseTextEntitiesPayload = {
  hyperlinkColor?: unknown;
  hyperlinkBold?: unknown;
  hyperlinkUnderline?: unknown;
  hyperlinkItalic?: unknown;
  highlightType?: unknown;
  highlightStatic?: unknown;
};

export const parseTextEntities = (
  content: unknown,
  payload: ParseTextEntitiesPayload | Record<string, unknown>,
): { updatedContent: string; originalContent: string } | null => {
  const {
    highlightType,
    highlightStatic,
    hyperlinkBold,
    hyperlinkUnderline,
    hyperlinkItalic,
  } = payload as ParseTextEntitiesPayload;
  const originalContent = content;
  if (!originalContent || typeof originalContent !== "string") return null;

  let hasChanges = false;

  const parser = new DOMParser();
  const doc = parser.parseFromString(
    `<div>${originalContent}</div>`,
    "text/html",
  );

  // Handle highlight animations
  if (typeof highlightType === "string" && highlightType) {
    const highlights = doc.querySelectorAll("[data-highlight-animation]");
    highlights.forEach((element) => {
      if (element instanceof HTMLElement) {
        const oldValue = element.getAttribute("data-highlight-animation");
        element.setAttribute("data-highlight-animation", highlightType);
        if (oldValue !== highlightType) hasChanges = true;
      }
    });
  }

  // Handle static highlights
  if (typeof highlightStatic === "boolean") {
    const staticHighlights = doc.querySelectorAll("[data-highlight-static]");
    staticHighlights.forEach((element) => {
      if (element instanceof HTMLElement) {
        const oldValue = element.getAttribute("data-highlight-static");
        const newValue = highlightStatic.toString();
        element.setAttribute("data-highlight-static", newValue);
        if (oldValue !== newValue) hasChanges = true;
      }
    });
  }

  // Handle hyperlink color — remove inline color so it inherits from CSS
  // variable. Use a presence check (`in`) so resetting the color to an empty
  // string still strips the inline override; a truthy check would skip
  // empty/false values and leave the link stuck on its old hard-coded color.
  if ("hyperlinkColor" in (payload as Record<string, unknown>)) {
    const links = doc.querySelectorAll("a");
    links.forEach((link) => {
      if (link instanceof HTMLElement && link.style.color) {
        link.style.removeProperty("color");
        if (!link.getAttribute("style")) link.removeAttribute("style");
        hasChanges = true;
      }
    });
  }

  // Handle hyperlink styling (bold/italic/underline)
  if (
    typeof hyperlinkBold === "boolean" ||
    typeof hyperlinkUnderline === "boolean" ||
    typeof hyperlinkItalic === "boolean"
  ) {
    const links = doc.querySelectorAll("a");

    links.forEach((link) => {
      if (typeof hyperlinkBold === "boolean") {
        if (applyInlineWrap(doc, link, "strong", hyperlinkBold as boolean)) {
          hasChanges = true;
        }
      }
      if (typeof hyperlinkUnderline === "boolean") {
        if (
          applyInlineWrap(doc, link, "u", hyperlinkUnderline as boolean)
        ) {
          hasChanges = true;
        }
      }
      if (typeof hyperlinkItalic === "boolean") {
        if (applyInlineWrap(doc, link, "em", hyperlinkItalic as boolean)) {
          hasChanges = true;
        }
      }
    });
  }

  if (!hasChanges) return null;

  const root = doc.body.firstChild;
  // Read-only innerHTML access — extracts the (now-mutated) content from the
  // wrapping <div> we created above. If DOMParser produced an unexpected tree
  // (e.g. a text node) we have nothing reliable to write back, so bail out
  // rather than returning the unmutated original under a "changed" claim.
  if (!(root instanceof HTMLElement)) return null;
  const updatedContent = root.innerHTML;

  return { updatedContent, originalContent };
};

/**
 * Wrap a link's children in `<tagName>` (when `enable` is true) or unwrap any
 * existing `<tagName>` descendants (when `enable` is false). Returns true when
 * the DOM changed.
 *
 * The wrap branch is idempotent: if the link is already a single `<tagName>`
 * wrapper around its children, it short-circuits. Callers re-run this on the
 * untouched original content each render, so preventing double-wrap matters.
 */
const applyInlineWrap = (
  doc: Document,
  link: HTMLAnchorElement,
  tagName: "strong" | "em" | "u",
  enable: boolean,
): boolean => {
  if (enable) {
    const onlyChild =
      link.children.length === 1 ? link.firstElementChild : null;
    const alreadyWrapped =
      !!onlyChild && onlyChild.tagName.toLowerCase() === tagName;
    if (alreadyWrapped) return false;

    const wrapper = doc.createElement(tagName);
    while (link.firstChild) {
      wrapper.appendChild(link.firstChild);
    }
    link.appendChild(wrapper);
    return true;
  }

  const existing = link.querySelectorAll(tagName);
  if (existing.length === 0) return false;
  existing.forEach((node) => {
    const parent = node.parentNode;
    while (node.firstChild) {
      parent?.insertBefore(node.firstChild, node);
    }
    node.remove();
  });
  return true;
};
